import { useEffect } from 'react';
import { useNeighborhoodContextSafe } from '../context';
import { isValidCoordinate } from '../utils/coordinates';

type ActiveListing = {
  id: string | undefined;
  geo: { lat: number; lon: number } | undefined;
};

/**
 * Triggers a commute recalculation when the active listing changes while a destination is set.
 *
 * Safe to call from multiple components — the singleton coalesces concurrent calls for the
 * same listing+destination key, and serializes superseding calls via an internal sequence number.
 */
export const useCommuteListingSync = (listing: ActiveListing | undefined): void => {
  const ctx = useNeighborhoodContextSafe();

  useEffect(() => {
    if (!ctx) return;
    if (!listing?.geo) return;
    if (!isValidCoordinate(listing.geo.lat, listing.geo.lon)) return;

    const listingId = listing.id ?? `geo:${listing.geo.lat},${listing.geo.lon}`;
    const { state, actions } = ctx;
    if (!state.commute.destination) return;
    if (state.commute.calculatedForListingId === listingId) return;

    void actions.recalcForListing({
      origin: { lat: listing.geo.lat, lng: listing.geo.lon },
      listingId,
    });
    // ctx is intentionally omitted from deps: actions are stable, and we want to
    // refire only on listing/geo changes — not on every state update from the singleton.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id, listing?.geo?.lat, listing?.geo?.lon]);
};
